import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Card, 
  CardBody, 
  CardHeader,
  Input,
  Button,
  Divider,
  Chip,
  Spacer
} from '@heroui/react';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [authMode, setAuthMode] = useState('firebase'); // 'firebase' or 'local'
  const { login } = useAuth();
  const navigate = useNavigate();

  const togglePasswordVisibility = () => setIsPasswordVisible(!isPasswordVisible);

  async function handleSubmit(e) {
    e.preventDefault();

    try {
      setError('');
      setLoading(true);
      await login(email, password, authMode === 'local');
      navigate('/'); // Navigate to dashboard or home page after login
    } catch (error) {
      let errorMsg = error.message || 'Failed to sign in';
      
      // Check if it was a firebase failure but the user exists locally
      if (authMode === 'firebase') {
        const lowerMsg = errorMsg.toLowerCase();
        if (lowerMsg.includes('user-not-found') || lowerMsg.includes('auth/user-not-found') || lowerMsg.includes('no user')) {
          try {
            const localUsers = JSON.parse(localStorage.getItem('local_users') || '[]');
            const hasLocalUser = localUsers.some(u => u.email.toLowerCase() === email.toLowerCase());
            if (hasLocalUser) {
              setError('Account not found in the Cloud. It looks like this email is registered as a Local Account. Switch to Local Account mode to sign in.');
              setLoading(false);
              return;
            }
          } catch (e) {
            console.error('Error checking local users on failure:', e);
          }
        }
      }
      setError('Failed to sign in: ' + errorMsg);
    }

    setLoading(false);
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 flex">
      {/* Left side - Welcome section */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-center p-12 text-white">
        <div className="max-w-md">
          <h1 className="text-5xl font-bold mb-6 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
            ApexionFlow
          </h1>
          <p className="text-xl text-gray-300 mb-12 leading-relaxed">
            Streamline your reporting process with our intuitive platform powered by ApexionX
          </p>

          <div className="space-y-6">
            <Card className="bg-white/10 backdrop-blur-sm border-white/20">
              <CardBody className="p-6">
                <h3 className="text-xl font-semibold mb-4 text-white">Key Features</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Chip size="sm" color="success" variant="flat">✓</Chip>
                    <span className="text-gray-300">AI-powered report generation</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Chip size="sm" color="success" variant="flat">✓</Chip>
                    <span className="text-gray-300">Supervisor & Lawyer review agents</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Chip size="sm" color="success" variant="flat">✓</Chip>
                    <span className="text-gray-300">Voice input capabilities</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Chip size="sm" color="success" variant="flat">✓</Chip>
                    <span className="text-gray-300">Enterprise-grade security</span>
                  </div>
                </div>
              </CardBody>
            </Card>

            <Card className="bg-white/10 backdrop-blur-sm border-white/20">
              <CardBody className="p-6">
                <h3 className="text-xl font-semibold mb-3 text-white">Why Choose Us</h3>
                <p className="text-gray-300 leading-relaxed">
                  The most comprehensive solution for law enforcement reporting needs, 
                  with industry-leading AI assistance and security.
                </p>
              </CardBody>
            </Card>
          </div>
        </div>
      </div>

      {/* Right side - Login form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <Card className="w-full max-w-md bg-white/95 backdrop-blur-sm shadow-2xl">
          <CardHeader className="flex flex-col gap-2 items-center pb-6">
            <h2 className="text-3xl font-bold text-gray-800">Welcome Back</h2>
            <p className="text-gray-600">Sign in to your account</p>
          </CardHeader>
          
          <CardBody className="space-y-6">
            {error && (
              <Chip color="danger" variant="flat" className="w-full p-3">
                {error}
              </Chip>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Auth Mode Toggle */}
              <div className="flex bg-gray-100 dark:bg-gray-800 p-1 rounded-xl mb-4 border border-gray-200 dark:border-gray-700 shadow-inner">
                <button
                  type="button"
                  onClick={() => setAuthMode('firebase')}
                  className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-all duration-200 ${
                    authMode === 'firebase'
                      ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                  }`}
                >
                  Cloud Account
                </button>
                <button
                  type="button"
                  onClick={() => setAuthMode('local')}
                  className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-all duration-200 ${
                    authMode === 'local'
                      ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                  }`}
                >
                  Local Account
                </button>
              </div>

              {authMode === 'local' && (
                <div className="text-xs text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/30 p-2.5 rounded-lg border border-blue-100 dark:border-blue-900/30 leading-relaxed mb-2">
                  ℹ️ <strong>Local Auth Mode:</strong> Stored on this device only. Use any email and password to log in or create a new account.
                </div>
              )}

              <Input
                type="email"
                label="Email"
                placeholder={authMode === 'local' ? "local-dev@example.com" : "deputy@example.com"}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                variant="bordered"
                size="lg"
                isRequired
                classNames={{
                  input: "text-gray-800",
                  label: "text-gray-700",
                }}
              />

              <Input
                label="Password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                variant="bordered"
                size="lg"
                isRequired
                endContent={
                  <button
                    className="focus:outline-none"
                    type="button"
                    onClick={togglePasswordVisibility}
                  >
                    {isPasswordVisible ? (
                      <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                    ) : (
                      <EyeIcon className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                }
                type={isPasswordVisible ? "text" : "password"}
                classNames={{
                  input: "text-gray-800",
                  label: "text-gray-700",
                }}
              />

              <Spacer y={2} />

              <Button
                type="submit"
                color="primary"
                size="lg"
                className="w-full font-semibold"
                isLoading={loading}
                disabled={loading}
              >
                {loading ? 'Signing in...' : authMode === 'local' ? 'Sign In Locally' : 'Sign In'}
              </Button>
            </form>

            <div className="text-center space-y-4">
              <Divider />
              
              <div className="space-y-2">
                <p className="text-gray-600">
                  Don't have an account?{' '}
                  <Link 
                    to="/signup" 
                    className="text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Sign up
                  </Link>
                </p>
                
                <Link 
                  to="/forgot-password" 
                  className="text-blue-600 hover:text-blue-800 font-medium block"
                >
                  Forgot password?
                </Link>
              </div>
            </div>

            <Divider />

            <div className="flex justify-center gap-4 text-sm">
              <Link 
                to="/terms" 
                className="text-gray-600 hover:text-gray-800 transition-colors"
              >
                Terms
              </Link>
              <span className="text-gray-400">•</span>
              <Link 
                to="/privacy" 
                className="text-gray-600 hover:text-gray-800 transition-colors"
              >
                Privacy
              </Link>
              <span className="text-gray-400">•</span>
              <Link 
                to="/security" 
                className="text-gray-600 hover:text-gray-800 transition-colors"
              >
                Security
              </Link>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}

export default Login;